import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { gql, useQuery } from '@apollo/client'
import { Alert, DatePicker, Divider, Space } from 'antd'
import dayjs from 'dayjs'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'
import Board from './Board'
import AssigneeOrdersDrawer from './AssigneeOrdersDrawer'
import CapacityForm from './CapacityForm'

const DATE_FORMAT = 'YYYY-MM-DD'
const UNASSIGNED = 'unassigned'

export const LEAD_DISTRIBUTION_BOARD_QUERY = gql`
  query LeadDistributionBoard($date: ISO8601Date) {
    currentStaff {
      id
      fullName
      staffRoles {
        id
        name
      }
    }
    leadDistributionBoard(date: $date) {
      assignee {
        id
        fullName
      }
      isDistributeSale
      leadAmountPerDay
      openCount
      remaining
      totalCount
      stateCounts {
        requestingQuotation
        waitingForSupplier
        waitingCustomerConfirm
        payment
        complete
        cancel
      }
    }
  }
`

const parseDate = (value) => {
  const date = value ? dayjs(value, DATE_FORMAT, true) : null
  return date && date.isValid() ? date : dayjs()
}

const normalizeStaffId = (value) => {
  if (!value) return null
  if (value === '0' || value === UNASSIGNED) return UNASSIGNED
  return value
}

const LeadDistributionsIndexPage = () => {
  const { i18n } = useTranslation('leadDistributions')
  const history = useHistory()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const date = parseDate(params.get('date'))
  const dateString = date.format(DATE_FORMAT)
  const staffId = normalizeStaffId(params.get('staffId'))

  useEffect(() => {
    document.title = `${i18n.t('leadDistributions:title')} | OSH CRM`
  }, [i18n.language])

  const { loading, data, error, refetch } = useQuery(LEAD_DISTRIBUTION_BOARD_QUERY, {
    variables: { date: dateString },
    fetchPolicy: 'cache-and-network'
  })

  const rows = (data && data.leadDistributionBoard) || []
  const currentStaff = (data && data.currentStaff) || null
  const isSuperAdmin = !!currentStaff && (currentStaff.staffRoles || []).some(role => role.name === 'super_admin')
  const currentStaffRow = currentStaff && rows.find(row => row.assignee && row.assignee.id === currentStaff.id)
  const selectedRow = staffId && rows.find(row => (
    staffId === UNASSIGNED ? !row.assignee : row.assignee && row.assignee.id === staffId
  ))

  const updateParams = (changes) => {
    const next = new URLSearchParams(location.search)
    Object.entries(changes).forEach(([key, value]) => {
      if (value) {
        next.set(key, value)
      } else {
        next.delete(key)
      }
    })
    const search = next.toString()
    history.push({ search: search ? `?${search}` : '' })
  }

  const onChangeDate = (value) => {
    updateParams({ date: value ? value.format(DATE_FORMAT) : null })
  }

  const onOpenAssignee = (row) => {
    updateParams({ date: dateString, staffId: row.assignee ? row.assignee.id : UNASSIGNED })
  }

  const onCloseDrawer = () => {
    updateParams({ staffId: null })
  }

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: '/lead_distributions', name: i18n.t('leadDistributions:title') }
      ]}>
        <ContentLayout>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16 }}>
            <Space>
              <span>{i18n.t('leadDistributions:date')}</span>
              <DatePicker allowClear={false} format={DATE_FORMAT} onChange={onChangeDate} value={date} />
            </Space>
            {currentStaff && (
              <CapacityForm
                key={`${currentStaff.id}-${currentStaffRow ? currentStaffRow.leadAmountPerDay : ''}`}
                initialValue={currentStaffRow ? currentStaffRow.leadAmountPerDay : null}
                label={i18n.t('leadDistributions:capacity.ownLabel')}
                onSaved={refetch}
              />
            )}
          </div>
          <Divider />
          {error && <Alert message={error.message} showIcon style={{ marginBottom: 16 }} type="error" />}
          <Board loading={loading} onOpenAssignee={onOpenAssignee} rows={rows} />
          <AssigneeOrdersDrawer
            assigneeId={staffId}
            date={dateString}
            isSuperAdmin={isSuperAdmin}
            onClose={onCloseDrawer}
            onCapacitySaved={refetch}
            row={selectedRow || null}
          />
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

export default LeadDistributionsIndexPage
